import {
  Box,
  Boxes,
  Camera,
  ChevronDown,
  Download,
  DraftingCompass,
  FileUp,
  LoaderCircle,
  MapPinned,
  Maximize2,
  SlidersHorizontal,
  Trash2
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { defaultDxfFileName, downloadDefaultDxfFile } from "../cad/default-dxf-file";
import { ModelAsset } from "../models/model-assets";
import { defaultBinRackLayerHeights } from "../objects/bin-rack";
import { defaultPalletRackLayerHeights } from "../objects/pallet-rack";
import type {
  CargoEditorCargoAsset,
  CargoEditorDeviceBatchSelection,
  CargoEditorDxfReference,
  CargoEditorDxfStage,
  CargoEditorPalletReciprocatingLiftBatchSelection,
  CargoEditorPalletStackerCraneBatchSelection,
  CargoEditorRackBatchSelection,
  CargoEditorRackConfiguration,
  CargoEditorRackType,
  CargoEditorSelection,
  CargoEditorSingleStationReciprocatingShuttleBatchSelection
} from "../scenes/cargo-editor-scene";
import type { CargoRenderOptions } from "../scenes/cargo-render-mode";

type DxfReferenceState = {
  activeFileName?: string;
  error?: string;
  loadingFileName?: string;
  metadata?: CargoEditorDxfReference;
  stage?: CargoEditorDxfStage;
};

type CargoSceneSettingsProps = {
  batchError?: string;
  batchSettingRegionCount: number;
  cargo: Array<{ asset?: CargoEditorCargoAsset; slotIndex: number }>;
  configuration: CargoEditorRackConfiguration;
  dxfReference: DxfReferenceState;
  disabled: boolean;
  exportError?: string;
  exporting: boolean;
  imageError?: string;
  includeCadInImage: boolean;
  onBrowseMode: () => void;
  onCargoChange: (slotIndex: number, asset?: CargoEditorCargoAsset) => Promise<void>;
  onDxfClear: () => void;
  onDxfFileChange: (file: File) => Promise<void>;
  onDeviceModelChange: (deviceId: string, modelId: string) => Promise<void>;
  onDeviceBatchModelChange: (
    selection: CargoEditorDeviceBatchSelection,
    modelId: string
  ) => Promise<void>;
  onExport: () => Promise<void>;
  onIncludeCadInImageChange: (includeCad: boolean) => void;
  onManageDevices: () => void;
  onPalletReciprocatingLiftLayerCountChange: (liftId: string, layerCount: number) => void;
  onPalletReciprocatingLiftBatchLayerCountChange: (
    selection: CargoEditorPalletReciprocatingLiftBatchSelection,
    layerCount: number
  ) => void;
  onRackBatchCargoCapacityChange: (
    selection: CargoEditorRackBatchSelection,
    cargoCapacity: number
  ) => Promise<void>;
  onRackBatchCargoChange: (
    selection: CargoEditorRackBatchSelection,
    asset?: CargoEditorCargoAsset
  ) => Promise<void>;
  onRackBatchLayerHeightsChange: (
    selection: CargoEditorRackBatchSelection,
    layerHeights: number[]
  ) => Promise<void>;
  onRackCargoChange: (asset?: CargoEditorCargoAsset) => Promise<void>;
  onRackConfigurationChange: (configuration: CargoEditorRackConfiguration) => void;
  onRenderOptionsChange: (options: CargoRenderOptions) => Promise<void>;
  onRenderImage: () => Promise<void>;
  onShowBatchSettingRegions: () => void;
  onSingleStationReciprocatingShuttleBatchHeightChange: (
    selection: CargoEditorSingleStationReciprocatingShuttleBatchSelection,
    height: number
  ) => void;
  onPalletStackerCraneBatchHeightChange: (
    selection: CargoEditorPalletStackerCraneBatchSelection,
    height: number
  ) => void;
  onSingleStationReciprocatingShuttleHeightChange: (shuttleId: string, height: number) => void;
  onPalletStackerCraneHeightChange: (stackerCraneId: string, height: number) => void;
  renderOptions: CargoRenderOptions;
  renderingImage: boolean;
  selection?: CargoEditorSelection;
};

type RackConfigurationDraft = {
  cargoCapacity: string;
  layerCount: string;
  layerHeights: string[];
};

const clearRackCargoValue = "clear-all";
type RackCargoChoice = CargoEditorCargoAsset | typeof clearRackCargoValue | "";
type CargoInspectorPanel = "cad" | "object" | "scene";

const palletCargoOptions: Array<{ label: string; value: CargoEditorCargoAsset | "" }> = [
  { label: "空", value: "" },
  { label: "木板货物", value: ModelAsset.WoodenCrate },
  { label: "钢卷", value: ModelAsset.SteelCoil }
];
const binCargoOptions: Array<{ label: string; value: CargoEditorCargoAsset | "" }> = [
  { label: "空", value: "" },
  { label: "料箱", value: ModelAsset.BinCargo }
];
const renderOptionLabels: Array<{
  hint?: string;
  key: keyof CargoRenderOptions;
  label: string;
  title?: string;
}> = [
  { key: "factoryLighting", label: "工厂灯光" },
  { key: "shadows", label: "阴影", title: "需同时开启工厂灯光" },
  { key: "environmentAndToneMapping", label: "环境光与色调映射" },
  {
    hint: "需配合工厂灯光或环境光与色调映射使用，否则模型会显示为一片黑色。",
    key: "pbrMaterials",
    label: "PBR 金属材质",
    title: "需搭配工厂灯光或环境光"
  },
  { key: "highDetailBinCargo", label: "料箱高精度模型" }
];

export function CargoSceneSettings({
  batchError,
  batchSettingRegionCount,
  cargo,
  configuration,
  dxfReference,
  disabled,
  exportError,
  exporting,
  imageError,
  includeCadInImage,
  onBrowseMode,
  onCargoChange,
  onDxfClear,
  onDxfFileChange,
  onDeviceModelChange,
  onDeviceBatchModelChange,
  onExport,
  onIncludeCadInImageChange,
  onManageDevices,
  onPalletReciprocatingLiftLayerCountChange,
  onPalletReciprocatingLiftBatchLayerCountChange,
  onRackBatchCargoCapacityChange,
  onRackBatchCargoChange,
  onRackBatchLayerHeightsChange,
  onRackCargoChange,
  onRackConfigurationChange,
  onRenderOptionsChange,
  onRenderImage,
  onShowBatchSettingRegions,
  onSingleStationReciprocatingShuttleBatchHeightChange,
  onPalletStackerCraneBatchHeightChange,
  onSingleStationReciprocatingShuttleHeightChange,
  onPalletStackerCraneHeightChange,
  renderOptions,
  renderingImage,
  selection
}: CargoSceneSettingsProps) {
  const [activePanel, setActivePanel] = useState<CargoInspectorPanel>("object");
  const [draft, setDraft] = useState<RackConfigurationDraft>(() =>
    createRackConfigurationDraft(configuration)
  );

  useEffect(() => {
    setDraft(createRackConfigurationDraft(configuration));
  }, [configuration]);

  useEffect(() => {
    if (selection) {
      setActivePanel("object");
    }
  }, [selection]);

  return (
    <aside className="cargo-settings" aria-label="场景设置">
      <header className="cargo-settings-header">
        <div className="cargo-settings-title">
          <div className="cargo-settings-eyebrow">HOLO / INSPECTOR</div>
          <h2>场景工作台</h2>
        </div>
        <div aria-label="设置分类" className="cargo-settings-tabs" role="tablist">
          <button
            aria-selected={activePanel === "object"}
            onClick={() => setActivePanel("object")}
            role="tab"
            type="button"
          >
            <Box aria-hidden="true" size={15} />
            对象
          </button>
          <button
            aria-selected={activePanel === "scene"}
            onClick={() => setActivePanel("scene")}
            role="tab"
            type="button"
          >
            <SlidersHorizontal aria-hidden="true" size={15} />
            场景
          </button>
          <button
            aria-selected={activePanel === "cad"}
            onClick={() => setActivePanel("cad")}
            role="tab"
            type="button"
          >
            <DraftingCompass aria-hidden="true" size={15} />
            CAD
          </button>
        </div>
      </header>

      {activePanel === "scene" ? (
        <div className="cargo-inspector-panel cargo-inspector-scene-panel" role="tabpanel">
          <section className="cargo-settings-section cargo-inspector-mode-section">
            <div className="cargo-settings-section-header">
              <h3>查看方式</h3>
              <span>演示与巡视</span>
            </div>
            <button
              className="cargo-browse-mode-button"
              disabled={disabled}
              onClick={onBrowseMode}
              type="button"
            >
              <Maximize2 aria-hidden="true" size={16} />
              进入浏览模式
            </button>
          </section>

          <RenderOptionsControls
            disabled={disabled}
            onChange={onRenderOptionsChange}
            options={renderOptions}
          />

          <section className="cargo-settings-section cargo-inspector-output-section">
            <div className="cargo-settings-section-header">
              <h3>输出</h3>
              <span>模型与图像</span>
            </div>
            <div className="cargo-inspector-output-actions">
              <button
                className="cargo-scene-export-button"
                disabled={disabled || !dxfReference.metadata}
                onClick={() => void onExport()}
                type="button"
              >
                {exporting ? (
                  <LoaderCircle aria-hidden="true" className="dxf-reference-spinner" size={16} />
                ) : (
                  <Download aria-hidden="true" size={16} />
                )}
                {exporting ? "导出中" : "导出 GLB"}
              </button>
              <button
                className="cargo-scene-export-button cargo-scene-image-button"
                disabled={disabled || !dxfReference.metadata}
                onClick={() => void onRenderImage()}
                type="button"
              >
                {renderingImage ? (
                  <LoaderCircle aria-hidden="true" className="dxf-reference-spinner" size={16} />
                ) : (
                  <Camera aria-hidden="true" size={16} />
                )}
                {renderingImage ? "生成中" : "生成场景图"}
              </button>
            </div>
            <label className="cargo-render-option cargo-scene-image-option">
              <span className="cargo-render-option-copy">
                <span>场景图包含 CAD 图纸</span>
              </span>
              <input
                checked={includeCadInImage}
                disabled={disabled}
                onChange={(event) => onIncludeCadInImageChange(event.target.checked)}
                type="checkbox"
              />
              <span aria-hidden="true" className="cargo-render-option-track">
                <span className="cargo-render-option-thumb" />
              </span>
            </label>
            <p className="cargo-scene-export-note">
              GLB 暂不包含料箱货物；生成 PNG 时会临时启用全部画质，并隐藏辅助轴与交互高亮。
            </p>
            {exportError || imageError ? (
              <p className="cargo-scene-export-error" role="alert">
                {exportError ?? imageError}
              </p>
            ) : null}
          </section>
        </div>
      ) : null}

      {activePanel === "cad" ? (
        <div className="cargo-inspector-panel cargo-inspector-cad-panel" role="tabpanel">
          <DxfReferenceControls
            disabled={disabled}
            onClear={onDxfClear}
            onFileChange={onDxfFileChange}
            state={dxfReference}
          />
        </div>
      ) : null}

      {activePanel === "object" ? (
        <div className="cargo-inspector-panel cargo-inspector-object-panel" role="tabpanel">
          <div className="cargo-inspector-object-actions">
            <button
              className="cargo-region-settings-button"
              disabled={disabled || !dxfReference.metadata}
              onClick={onShowBatchSettingRegions}
              type="button"
            >
              <MapPinned aria-hidden="true" size={16} />
              区域设定
              <span>{batchSettingRegionCount}</span>
            </button>
            <button
              className="cargo-device-library-button"
              onClick={onManageDevices}
              title="管理设备模型"
              type="button"
            >
              <Boxes aria-hidden="true" size={16} />
              设备库
            </button>
          </div>

          {!selection ? (
            <section className="cargo-settings-empty" aria-labelledby="cargo-settings-guide-title">
              <div>
                <h3 id="cargo-settings-guide-title">选择需要编辑的对象</h3>
                <p className="cargo-settings-guide-intro">
                  货仓、设备、提升机和穿梭车都可以双击选择，右侧会展示对应的编辑项。
                </p>
              </div>

              <ol className="cargo-settings-guide">
                <li>
                  <span className="cargo-settings-guide-number">01</span>
                  <div>
                    <strong>移动鼠标，定位货仓</strong>
                    <p>
                      将鼠标移到货架前后两排立柱之间的货仓空间。出现紫色半透明立方体时，表示鼠标已经命中这个完整货仓单元。
                    </p>
                    <span className="cargo-settings-guide-cue">
                      <span
                        aria-hidden="true"
                        className="cargo-settings-guide-swatch cargo-settings-guide-swatch-hover"
                      />
                      紫色半透明：鼠标当前指向的货仓
                    </span>
                  </div>
                </li>
                <li>
                  <span className="cargo-settings-guide-number">02</span>
                  <div>
                    <strong>在紫色区域上快速双击鼠标左键</strong>
                    <p>
                      选中后，货仓会显示较小的莫兰迪橙色半透明立方体和黑白交替边框；整个货架同时出现莫兰迪蓝色包围盒。
                    </p>
                    <span className="cargo-settings-guide-cue">
                      <span
                        aria-hidden="true"
                        className="cargo-settings-guide-swatch cargo-settings-guide-swatch-selected"
                      />
                      橙色加黑白边框：当前选中的货仓
                    </span>
                    <span className="cargo-settings-guide-cue">
                      <span
                        aria-hidden="true"
                        className="cargo-settings-guide-swatch cargo-settings-guide-swatch-rack"
                      />
                      蓝色半透明：当前货仓所属的货架
                    </span>
                  </div>
                </li>
                <li>
                  <span className="cargo-settings-guide-number">03</span>
                  <div>
                    <strong>在右侧修改货仓和货架</strong>
                    <p>
                      选中后，此处会按当前容量展示全部货位。横梁式货架可设置木板货物、钢卷或空，料箱货架只能设置料箱或空。货位数量作用于当前货仓，层数和各层层高作用于蓝色包围盒对应的整组货架，不会修改相邻货架。
                      新导入的料箱货架默认生成 10 层，每层 0.66m，并按 DXF
                      中识别到的排数铺满全部料箱货位。
                    </p>
                  </div>
                </li>
                <li>
                  <span className="cargo-settings-guide-number">04</span>
                  <div>
                    <strong>双击设备切换三维型号</strong>
                    <p>
                      将鼠标放在叉车、CTU
                      等设备模型的实体表面并快速双击。设备出现白色轮廓后，右侧会显示其 DXF
                      图块匹配前缀，以及该前缀下已经上传的全部设备型号；选择型号后只替换当前设备实例，不改变位置和朝向。
                    </p>
                  </div>
                </li>
                <li>
                  <span className="cargo-settings-guide-number">05</span>
                  <div>
                    <strong>双击提升机修改层数</strong>
                    <p>
                      将鼠标放在托盘往复式提升机模型的实体表面并快速双击。出现白色轮廓后，可在右侧即时修改当前提升机层数；默认生成
                      4 层，每层高度固定为 2000 mm。
                    </p>
                  </div>
                </li>
                <li>
                  <span className="cargo-settings-guide-number">06</span>
                  <div>
                    <strong>双击穿梭车修改车身高度</strong>
                    <p>
                      将鼠标放在单工位往复式穿梭车的轨道、车身、顶部或其间空隙并快速双击。出现白色轮廓后，可修改车身高度；顶部会继续贴合车身，轨道长度由
                      DXF 图块决定。
                    </p>
                  </div>
                </li>
                <li>
                  <span className="cargo-settings-guide-number">07</span>
                  <div>
                    <strong>切换多选模式进行批量修改</strong>
                    <p>
                      点击场景左上角的多选图标，或按 B
                      键切换到多选模式。鼠标左键单击货架、设备、提升机或穿梭车即可加入选择，再次单击已经选中的对象即可取消。第一个选中的对象会确定本次队列的类别，后续只能加入同类对象；取消队首后由下一项接替，全部取消后可重新选择任意类别。再次按
                      B 或按 Esc 返回单选模式，并清除本次全部选中状态。
                    </p>
                  </div>
                </li>
              </ol>
            </section>
          ) : selection.kind === "device-batch" ? (
            <DeviceBatchSettings
              disabled={disabled}
              error={batchError}
              onModelChange={onDeviceBatchModelChange}
              selection={selection}
            />
          ) : selection.kind === "pallet-reciprocating-lift-batch" ? (
            <PalletReciprocatingLiftBatchSettings
              disabled={disabled}
              error={batchError}
              onLayerCountChange={onPalletReciprocatingLiftBatchLayerCountChange}
              selection={selection}
            />
          ) : selection.kind === "pallet-stacker-crane-batch" ? (
            <PalletStackerCraneBatchSettings
              disabled={disabled}
              error={batchError}
              onHeightChange={onPalletStackerCraneBatchHeightChange}
              selection={selection}
            />
          ) : selection.kind === "single-station-reciprocating-shuttle-batch" ? (
            <SingleStationReciprocatingShuttleBatchSettings
              disabled={disabled}
              error={batchError}
              onHeightChange={onSingleStationReciprocatingShuttleBatchHeightChange}
              selection={selection}
            />
          ) : selection.kind === "rack-batch" ? (
            <RackBatchSettings
              disabled={disabled}
              error={batchError}
              onCargoCapacityChange={onRackBatchCargoCapacityChange}
              onCargoChange={onRackBatchCargoChange}
              onLayerHeightsChange={onRackBatchLayerHeightsChange}
              selection={selection}
            />
          ) : selection.kind === "device" ? (
            <div className="cargo-settings-content">
              <section className="cargo-settings-section">
                <div className="cargo-settings-section-header">
                  <h3>设备型号</h3>
                  <span>当前设备</span>
                </div>
                <div className="cargo-settings-device-prefix">
                  <span>DXF 图块匹配前缀</span>
                  <code>{selection.blockPrefix}</code>
                </div>
                <label className="cargo-settings-field">
                  <span>展示模型</span>
                  <select
                    disabled={disabled}
                    onChange={(event) =>
                      void onDeviceModelChange(selection.deviceId, event.target.value)
                    }
                    value={selection.modelId}
                  >
                    {selection.models.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.displayName}
                      </option>
                    ))}
                  </select>
                </label>
                <p className="cargo-settings-helper">
                  同一图块前缀下，数据库列表中的第一个模型用于设备初次导入；此处切换会自动保存到当前项目。
                </p>
              </section>
            </div>
          ) : selection.kind === "pallet-reciprocating-lift" ? (
            <div className="cargo-settings-content">
              <section className="cargo-settings-section cargo-settings-configuration-section">
                <div className="cargo-settings-section-header">
                  <h3>托盘往复式提升机</h3>
                  <span>当前设备</span>
                </div>
                <label className="cargo-settings-field">
                  <span>层数</span>
                  <input
                    disabled={disabled}
                    min="1"
                    onChange={(event) => {
                      const layerCount = Number(event.target.value);
                      if (Number.isInteger(layerCount) && layerCount > 0) {
                        onPalletReciprocatingLiftLayerCountChange(selection.liftId, layerCount);
                      }
                    }}
                    step="1"
                    type="number"
                    value={selection.layerCount}
                  />
                </label>
                <p className="cargo-settings-helper">
                  默认 4 层，每层高度固定为 2000 mm；修改后只更新当前选中的提升机。
                </p>
              </section>
            </div>
          ) : selection.kind === "pallet-stacker-crane" ? (
            <div className="cargo-settings-content">
              <section className="cargo-settings-section cargo-settings-configuration-section">
                <div className="cargo-settings-section-header">
                  <h3>托盘堆垛机</h3>
                  <span>当前设备</span>
                </div>
                <label className="cargo-settings-field">
                  <span>立柱高度 (mm)</span>
                  <input
                    disabled={disabled}
                    min="1"
                    onChange={(event) => {
                      const heightMillimeters = Number(event.target.value);
                      if (Number.isFinite(heightMillimeters) && heightMillimeters > 0) {
                        onPalletStackerCraneHeightChange(
                          selection.stackerCraneId,
                          heightMillimeters / 1000
                        );
                      }
                    }}
                    step="1"
                    type="number"
                    value={Math.round(selection.height * 1000)}
                  />
                </label>
                <p className="cargo-settings-helper">
                  默认立柱高度 8000 mm。顶部与天轨随立柱高度移动，载货台底面位于立柱高度的 36%
                  处；天轨和地轨长度由 DXF 图块边界决定。
                </p>
              </section>
            </div>
          ) : selection.kind === "single-station-reciprocating-shuttle" ? (
            <div className="cargo-settings-content">
              <section className="cargo-settings-section cargo-settings-configuration-section">
                <div className="cargo-settings-section-header">
                  <h3>单工位往复式穿梭车</h3>
                  <span>当前设备</span>
                </div>
                <label className="cargo-settings-field">
                  <span>车身高度 (mm)</span>
                  <input
                    disabled={disabled}
                    min="1"
                    onChange={(event) => {
                      const heightMillimeters = Number(event.target.value);
                      if (Number.isFinite(heightMillimeters) && heightMillimeters > 0) {
                        onSingleStationReciprocatingShuttleHeightChange(
                          selection.shuttleId,
                          heightMillimeters / 1000
                        );
                      }
                    }}
                    step="1"
                    type="number"
                    value={Math.round(selection.height * 1000)}
                  />
                </label>
                <p className="cargo-settings-helper">
                  默认 400 mm。车身沿 Y 轴缩放，顶部始终贴合车身上表面；轨道长度保持由 DXF
                  图块边界控制。
                </p>
              </section>
            </div>
          ) : (
            <div className="cargo-settings-content">
              <section className="cargo-settings-section">
                <div className="cargo-settings-section-header">
                  <h3>货仓内容</h3>
                  <span>
                    {selection.rackType === "bin" ? "料箱货架" : "横梁式货架"} · 第{" "}
                    {selection.cellIndex + 1} 仓 · 第 {selection.heightIndex + 1} 层
                  </span>
                </div>

                <div className="cargo-settings-slot-grid">
                  {cargo.map(({ asset, slotIndex }) => (
                    <CargoSelect
                      disabled={disabled}
                      key={`cargo-slot-${slotIndex}`}
                      label={`货位 ${slotIndex + 1}`}
                      onChange={(nextAsset) => onCargoChange(slotIndex, nextAsset)}
                      rackType={selection.rackType}
                      value={asset}
                    />
                  ))}
                </div>
              </section>

              <section className="cargo-settings-section cargo-settings-rack-cargo-section">
                <div className="cargo-settings-section-header">
                  <h3>整架货物</h3>
                  <span>覆盖全部货仓</span>
                </div>
                <div className="cargo-settings-rack-cargo-controls">
                  <label className="cargo-settings-field">
                    <span>统一设置</span>
                    <div className="cargo-settings-select">
                      <select
                        disabled={disabled}
                        onChange={(event) => {
                          const choice = event.target.value as RackCargoChoice;
                          if (!choice) {
                            return;
                          }
                          void onRackCargoChange(
                            choice === clearRackCargoValue
                              ? undefined
                              : parseCargoAsset(choice, selection.rackType)
                          );
                        }}
                        value=""
                      >
                        <option value="">选择操作</option>
                        <option value={clearRackCargoValue}>清空所有货物</option>
                        {(selection.rackType === "bin" ? binCargoOptions : palletCargoOptions)
                          .filter(({ value }) => value)
                          .map((option) => (
                            <option key={option.value} value={option.value}>
                              全部设置为{option.label}
                            </option>
                          ))}
                      </select>
                      <ChevronDown aria-hidden="true" size={16} strokeWidth={1.8} />
                    </div>
                  </label>
                </div>
                <p className="cargo-settings-helper">
                  选择后立即覆盖当前货架所有层、所有货仓和全部货位中已经设置的内容。
                </p>
              </section>

              <section className="cargo-settings-section cargo-settings-configuration-section">
                <div className="cargo-settings-section-header">
                  <h3>货架配置</h3>
                  <span>当前货架</span>
                </div>

                <div className="cargo-settings-readonly-grid">
                  <ReadonlyValue label="横向货仓" value={`${configuration.lengthCellCount} 个`} />
                  <ReadonlyValue
                    label="当前仓长度"
                    value={`${Math.round(configuration.cargoCellLength * 1000)} mm`}
                  />
                  <ReadonlyValue
                    label="当前仓宽度"
                    value={`${Math.round(configuration.cargoCellWidth * 1000)} mm`}
                  />
                  {selection.rackType === "bin" ? (
                    <ReadonlyValue label="料箱排数" value={`${configuration.cargoRowCount} 排`} />
                  ) : null}
                </div>

                <div className="cargo-settings-editable-grid">
                  <label className="cargo-settings-field">
                    <span>当前仓货位</span>
                    <input
                      disabled={disabled}
                      min="1"
                      onChange={(event) => {
                        const nextDraft = { ...draft, cargoCapacity: event.target.value };
                        updateRackConfigurationDraft(
                          nextDraft,
                          configuration,
                          setDraft,
                          onRackConfigurationChange
                        );
                      }}
                      step="1"
                      type="number"
                      value={draft.cargoCapacity}
                    />
                  </label>

                  <label className="cargo-settings-field">
                    <span>货架层数</span>
                    <input
                      disabled={disabled}
                      min="1"
                      onChange={(event) => {
                        const layerCount = Number(event.target.value);
                        const layerHeights = [...draft.layerHeights];
                        if (Number.isInteger(layerCount) && layerCount > 0) {
                          layerHeights.length = Math.min(layerHeights.length, layerCount);
                          while (layerHeights.length < layerCount) {
                            layerHeights.push(
                              String(getDefaultLayerHeightMillimeters(selection.rackType))
                            );
                          }
                        }
                        const nextDraft = {
                          ...draft,
                          layerCount: event.target.value,
                          layerHeights
                        };
                        updateRackConfigurationDraft(
                          nextDraft,
                          configuration,
                          setDraft,
                          onRackConfigurationChange
                        );
                      }}
                      step="1"
                      type="number"
                      value={draft.layerCount}
                    />
                  </label>
                </div>

                <div className="cargo-settings-layer-section">
                  <div className="cargo-settings-subsection-header">
                    <strong>各层净层高</strong>
                    <span>不含横杆；减少层数会清除顶部被移除层的货物</span>
                  </div>
                  <div className="cargo-settings-layer-grid">
                    {draft.layerHeights.map((height, index) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: Layers are only appended to or removed from the top, so their index is their identity.
                      <label className="cargo-settings-field" key={`layer-height-${index}`}>
                        <span>第 {index + 1} 层净高 (mm)</span>
                        <input
                          disabled={disabled}
                          min="1"
                          onChange={(event) => {
                            const layerHeights = [...draft.layerHeights];
                            layerHeights[index] = event.target.value;
                            const nextDraft = { ...draft, layerHeights };
                            updateRackConfigurationDraft(
                              nextDraft,
                              configuration,
                              setDraft,
                              onRackConfigurationChange
                            );
                          }}
                          step="1"
                          type="number"
                          value={height}
                        />
                      </label>
                    ))}
                  </div>
                </div>
              </section>
            </div>
          )}
        </div>
      ) : null}
    </aside>
  );
}

function DeviceBatchSettings({
  disabled,
  error,
  onModelChange,
  selection
}: {
  disabled: boolean;
  error?: string;
  onModelChange: (selection: CargoEditorDeviceBatchSelection, modelId: string) => Promise<void>;
  selection: CargoEditorDeviceBatchSelection;
}) {
  return (
    <div className="cargo-settings-content">
      <section className="cargo-settings-section">
        <BatchSettingsHeader count={selection.deviceIds.length} title="批量设备型号" />
        <div className="cargo-settings-device-prefix">
          <span>DXF 图块匹配前缀</span>
          <code>{selection.blockPrefix}</code>
        </div>
        <label className="cargo-settings-field">
          <span>统一展示模型</span>
          <select
            disabled={disabled}
            onChange={(event) => {
              if (event.target.value) {
                void onModelChange(selection, event.target.value);
              }
            }}
            value={selection.modelMixed ? "" : selection.modelId}
          >
            {selection.modelMixed ? <option value="">参数不一致，请选择</option> : null}
            {selection.models.map((model) => (
              <option key={model.id} value={model.id}>
                {model.displayName}
              </option>
            ))}
          </select>
        </label>
        <BatchSettingsNote error={error} mixed={selection.modelMixed} />
      </section>
    </div>
  );
}

function PalletReciprocatingLiftBatchSettings({
  disabled,
  error,
  onLayerCountChange,
  selection
}: {
  disabled: boolean;
  error?: string;
  onLayerCountChange: (
    selection: CargoEditorPalletReciprocatingLiftBatchSelection,
    layerCount: number
  ) => void;
  selection: CargoEditorPalletReciprocatingLiftBatchSelection;
}) {
  return (
    <div className="cargo-settings-content">
      <section className="cargo-settings-section cargo-settings-configuration-section">
        <BatchSettingsHeader count={selection.liftIds.length} title="批量提升机配置" />
        <label className="cargo-settings-field">
          <span>统一层数</span>
          <input
            disabled={disabled}
            min="1"
            onChange={(event) => {
              const layerCount = Number(event.target.value);
              if (Number.isInteger(layerCount) && layerCount > 0) {
                onLayerCountChange(selection, layerCount);
              }
            }}
            step="1"
            type="number"
            value={selection.layerCount}
          />
        </label>
        <BatchSettingsNote error={error} mixed={selection.layerCountMixed} />
      </section>
    </div>
  );
}

function SingleStationReciprocatingShuttleBatchSettings({
  disabled,
  error,
  onHeightChange,
  selection
}: {
  disabled: boolean;
  error?: string;
  onHeightChange: (
    selection: CargoEditorSingleStationReciprocatingShuttleBatchSelection,
    height: number
  ) => void;
  selection: CargoEditorSingleStationReciprocatingShuttleBatchSelection;
}) {
  return (
    <div className="cargo-settings-content">
      <section className="cargo-settings-section cargo-settings-configuration-section">
        <BatchSettingsHeader count={selection.shuttleIds.length} title="批量穿梭车配置" />
        <label className="cargo-settings-field">
          <span>统一车身高度 (mm)</span>
          <input
            disabled={disabled}
            min="1"
            onChange={(event) => {
              const heightMillimeters = Number(event.target.value);
              if (Number.isFinite(heightMillimeters) && heightMillimeters > 0) {
                onHeightChange(selection, heightMillimeters / 1000);
              }
            }}
            step="1"
            type="number"
            value={Math.round(selection.height * 1000)}
          />
        </label>
        <BatchSettingsNote error={error} mixed={selection.heightMixed} />
      </section>
    </div>
  );
}
function PalletStackerCraneBatchSettings({
  disabled,
  error,
  onHeightChange,
  selection
}: {
  disabled: boolean;
  error?: string;
  onHeightChange: (selection: CargoEditorPalletStackerCraneBatchSelection, height: number) => void;
  selection: CargoEditorPalletStackerCraneBatchSelection;
}) {
  return (
    <div className="cargo-settings-content">
      <section className="cargo-settings-section cargo-settings-configuration-section">
        <BatchSettingsHeader count={selection.stackerCraneIds.length} title="批量堆垛机配置" />
        <label className="cargo-settings-field">
          <span>统一立柱高度 (mm)</span>
          <input
            disabled={disabled}
            min="1"
            onChange={(event) => {
              const heightMillimeters = Number(event.target.value);
              if (Number.isFinite(heightMillimeters) && heightMillimeters > 0) {
                onHeightChange(selection, heightMillimeters / 1000);
              }
            }}
            step="1"
            type="number"
            value={Math.round(selection.height * 1000)}
          />
        </label>
        <BatchSettingsNote error={error} mixed={selection.heightMixed} />
      </section>
    </div>
  );
}

function RackBatchSettings({
  disabled,
  error,
  onCargoCapacityChange,
  onCargoChange,
  onLayerHeightsChange,
  selection
}: {
  disabled: boolean;
  error?: string;
  onCargoCapacityChange: (
    selection: CargoEditorRackBatchSelection,
    cargoCapacity: number
  ) => Promise<void>;
  onCargoChange: (
    selection: CargoEditorRackBatchSelection,
    asset?: CargoEditorCargoAsset
  ) => Promise<void>;
  onLayerHeightsChange: (
    selection: CargoEditorRackBatchSelection,
    layerHeights: number[]
  ) => Promise<void>;
  selection: CargoEditorRackBatchSelection;
}) {
  const [draft, setDraft] = useState<RackConfigurationDraft>(() => ({
    cargoCapacity: String(selection.cargoCapacity),
    layerCount: String(selection.layerHeights.length),
    layerHeights: selection.layerHeights.map((height) => String(Math.round(height * 1000)))
  }));

  useEffect(() => {
    setDraft({
      cargoCapacity: String(selection.cargoCapacity),
      layerCount: String(selection.layerHeights.length),
      layerHeights: selection.layerHeights.map((height) => String(Math.round(height * 1000)))
    });
  }, [selection]);

  const applyLayerDraft = (nextDraft: RackConfigurationDraft) => {
    setDraft(nextDraft);
    const layerCount = Number(nextDraft.layerCount);
    const heights = nextDraft.layerHeights.map(Number);
    if (
      Number.isInteger(layerCount) &&
      layerCount > 0 &&
      heights.length === layerCount &&
      heights.every((height) => Number.isInteger(height) && height > 0)
    ) {
      void onLayerHeightsChange(
        selection,
        heights.map((height) => height / 1000)
      );
    }
  };

  return (
    <div className="cargo-settings-content">
      <section className="cargo-settings-section cargo-settings-rack-cargo-section">
        <BatchSettingsHeader
          count={selection.rackIds.length}
          title={selection.rackType === "bin" ? "批量料箱货架" : "批量横梁式货架"}
        />
        <label className="cargo-settings-field">
          <span>统一全部货仓</span>
          <div className="cargo-settings-select">
            <select
              disabled={disabled}
              onChange={(event) => {
                const choice = event.target.value as RackCargoChoice;
                if (choice) {
                  void onCargoChange(
                    selection,
                    choice === clearRackCargoValue
                      ? undefined
                      : parseCargoAsset(choice, selection.rackType)
                  );
                }
              }}
              value=""
            >
              <option value="">选择操作</option>
              <option value={clearRackCargoValue}>清空所有货物</option>
              {(selection.rackType === "bin" ? binCargoOptions : palletCargoOptions)
                .filter(({ value }) => value)
                .map((option) => (
                  <option key={option.value} value={option.value}>
                    全部设置为{option.label}
                  </option>
                ))}
            </select>
            <ChevronDown aria-hidden="true" size={16} strokeWidth={1.8} />
          </div>
        </label>
        <p className="cargo-settings-helper">选择后立即覆盖当前多选货架的全部货仓和货位。</p>
      </section>

      <section className="cargo-settings-section cargo-settings-configuration-section">
        <div className="cargo-settings-section-header">
          <h3>统一货架参数</h3>
          <span>仅修改当前字段</span>
        </div>
        <div className="cargo-settings-editable-grid">
          <label className="cargo-settings-field">
            <span>每仓货位</span>
            <input
              disabled={disabled}
              min="1"
              onChange={(event) => {
                const cargoCapacity = Number(event.target.value);
                setDraft((current) => ({ ...current, cargoCapacity: event.target.value }));
                if (Number.isInteger(cargoCapacity) && cargoCapacity > 0) {
                  void onCargoCapacityChange(selection, cargoCapacity);
                }
              }}
              step="1"
              type="number"
              value={draft.cargoCapacity}
            />
          </label>
          <label className="cargo-settings-field">
            <span>货架层数</span>
            <input
              disabled={disabled}
              min="1"
              onChange={(event) => {
                const layerCount = Number(event.target.value);
                const layerHeights = [...draft.layerHeights];
                if (Number.isInteger(layerCount) && layerCount > 0) {
                  layerHeights.length = Math.min(layerHeights.length, layerCount);
                  while (layerHeights.length < layerCount) {
                    layerHeights.push(String(getDefaultLayerHeightMillimeters(selection.rackType)));
                  }
                }
                applyLayerDraft({ ...draft, layerCount: event.target.value, layerHeights });
              }}
              step="1"
              type="number"
              value={draft.layerCount}
            />
          </label>
        </div>
        <div className="cargo-settings-layer-section">
          <div className="cargo-settings-subsection-header">
            <strong>统一各层净层高</strong>
            <span>不含横杆；减少层数会清除被移除层的货物</span>
          </div>
          <div className="cargo-settings-layer-grid">
            {draft.layerHeights.map((height, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: Layers are only appended to or removed from the top, so their index is their identity.
              <label className="cargo-settings-field" key={`batch-layer-height-${index}`}>
                <span>第 {index + 1} 层净高 (mm)</span>
                <input
                  disabled={disabled}
                  min="1"
                  onChange={(event) => {
                    const layerHeights = [...draft.layerHeights];
                    layerHeights[index] = event.target.value;
                    applyLayerDraft({ ...draft, layerHeights });
                  }}
                  step="1"
                  type="number"
                  value={height}
                />
              </label>
            ))}
          </div>
        </div>
        <BatchSettingsNote
          error={error}
          mixed={selection.cargoCapacityMixed || selection.layerHeightsMixed}
        />
      </section>
    </div>
  );
}

function BatchSettingsHeader({ count, title }: { count: number; title: string }) {
  return (
    <div className="cargo-settings-section-header cargo-settings-batch-header">
      <h3>{title}</h3>
      <span>{count} 个已选</span>
    </div>
  );
}

function BatchSettingsNote({ error, mixed }: { error?: string; mixed: boolean }) {
  return (
    <>
      {mixed ? (
        <p className="cargo-settings-batch-note">
          选中对象的参数不一致。面板显示该类型默认值；只有你实际修改的字段会统一应用。
        </p>
      ) : null}
      {error ? (
        <p className="cargo-scene-export-error" role="alert">
          {error}
        </p>
      ) : null}
    </>
  );
}

function RenderOptionsControls({
  disabled,
  onChange,
  options
}: {
  disabled: boolean;
  onChange: (options: CargoRenderOptions) => Promise<void>;
  options: CargoRenderOptions;
}) {
  return (
    <section className="cargo-settings-section cargo-render-options">
      <div className="cargo-settings-section-header">
        <h3>渲染选项</h3>
        <span>默认关闭</span>
      </div>
      <div className="cargo-render-options-list">
        {renderOptionLabels.map(({ hint, key, label, title }) => (
          <label className="cargo-render-option" key={key} title={title}>
            <span className="cargo-render-option-copy">
              <span>{label}</span>
              {hint && <small>{hint}</small>}
            </span>
            <input
              checked={options[key]}
              disabled={disabled}
              onChange={(event) => {
                void onChange({ ...options, [key]: event.target.checked });
              }}
              type="checkbox"
            />
            <span aria-hidden="true" className="cargo-render-option-track">
              <span className="cargo-render-option-thumb" />
            </span>
          </label>
        ))}
      </div>
    </section>
  );
}

function DxfReferenceControls({
  disabled,
  onClear,
  onFileChange,
  state
}: {
  disabled: boolean;
  onClear: () => void;
  onFileChange: (file: File) => Promise<void>;
  state: DxfReferenceState;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDownloadingSample, setIsDownloadingSample] = useState(false);
  const isLoading = Boolean(state.loadingFileName);
  const hasReference = Boolean(state.activeFileName && state.metadata);

  const handleSampleDownload = async () => {
    setIsDownloadingSample(true);
    try {
      await downloadDefaultDxfFile();
    } catch (error) {
      console.error("Failed to download the sample DXF file", error);
    } finally {
      setIsDownloadingSample(false);
    }
  };

  return (
    <section className="cargo-settings-section dxf-reference-section">
      <div className="cargo-settings-section-header">
        <h3>CAD 定位图</h3>
        <span>水平面参考</span>
      </div>

      <input
        accept=".dxf,application/dxf,image/vnd.dxf"
        className="dxf-reference-input"
        disabled={disabled}
        onChange={(event) => {
          const file = event.currentTarget.files?.[0];
          event.currentTarget.value = "";
          if (file) {
            void onFileChange(file).catch((error: unknown) => {
              console.error("Failed to replace project DXF", error);
            });
          }
        }}
        ref={inputRef}
        type="file"
      />

      <div className="dxf-reference-actions">
        <button
          className="dxf-reference-button dxf-reference-button-primary"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          type="button"
        >
          {isLoading ? (
            <LoaderCircle aria-hidden="true" className="dxf-reference-spinner" size={16} />
          ) : (
            <FileUp aria-hidden="true" size={16} />
          )}
          {hasReference ? "替换图纸" : "选择 DXF"}
        </button>
        <button
          aria-label={`下载示例${defaultDxfFileName}`}
          className="dxf-reference-button dxf-reference-button-icon"
          disabled={isDownloadingSample}
          onClick={() => void handleSampleDownload()}
          title={
            isDownloadingSample
              ? `正在准备示例${defaultDxfFileName}`
              : `下载示例${defaultDxfFileName}`
          }
          type="button"
        >
          {isDownloadingSample ? (
            <LoaderCircle aria-hidden="true" className="dxf-reference-spinner" size={16} />
          ) : (
            <Download aria-hidden="true" size={16} />
          )}
        </button>
        {hasReference || isLoading ? (
          <button
            aria-label={isLoading ? "取消导入并清除图纸" : "清除图纸"}
            className="dxf-reference-button dxf-reference-button-icon"
            disabled={disabled}
            onClick={onClear}
            title={isLoading ? "取消导入并清除图纸" : "清除图纸"}
            type="button"
          >
            <Trash2 aria-hidden="true" size={16} />
          </button>
        ) : null}
      </div>

      <div className="dxf-reference-status" aria-live="polite">
        {isLoading ? (
          <>
            <strong title={state.loadingFileName}>{state.loadingFileName}</strong>
            <span>{formatDxfStage(state.stage)}</span>
          </>
        ) : hasReference && state.metadata ? (
          <>
            <strong title={state.activeFileName}>{state.activeFileName}</strong>
            <span>
              {formatMeters(state.metadata.worldWidth)} × {formatMeters(state.metadata.worldDepth)}{" "}
              · {state.metadata.pixelWidth} × {state.metadata.pixelHeight}px ·{" "}
              {state.metadata.unitLabel} · 横梁式货架 {state.metadata.palletRackCount} 组 · 料箱货架{" "}
              {state.metadata.binRackCount} 组 · 双链输送机{" "}
              {state.metadata.doubleChainConveyorCount} 台 · 辊道输送机{" "}
              {state.metadata.rollerConveyorCount} 台 · 辊道顶升移载{" "}
              {state.metadata.rollerLiftTransferCount} 台 · 托盘往复式提升机{" "}
              {state.metadata.palletReciprocatingLiftCount} 台 · 单工位往复式穿梭车{" "}
              {state.metadata.singleStationReciprocatingShuttleCount} 台 · 托盘堆垛机{" "}
              {state.metadata.palletStackerCraneCount} 台 · 批量设定区域{" "}
              {state.metadata.batchSettingRegionCount} 个
            </span>
          </>
        ) : (
          <span>未加载定位图</span>
        )}
      </div>

      <details className="dxf-protocol-guide">
        <summary>
          <span>CAD 标注协议</span>
          <ChevronDown aria-hidden="true" className="dxf-protocol-summary-icon" size={16} />
        </summary>

        <div className="dxf-protocol-guide-content">
          <p className="dxf-protocol-guide-intro">
            程序扫描所有可见且未冻结图层中的顶层图块，并递归查找普通组合块中的两类货仓和货物标记，不要求创建专用元数据图层。设备、三类输送设备、托盘往复式提升机和单工位往复式穿梭车通常只读取顶层块；放入“批量设定区域”组合块后也会被展开识别。三维定位来自图块内部实体的真实边界，普通线框、文字和尺寸标注只作为水平面参考。
          </p>

          <ol className="dxf-protocol-steps">
            <li>
              <span className="dxf-protocol-step-number">01</span>
              <div>
                <strong>准备参与识别的图层</strong>
                <p>
                  目标块可以放在任意图层，无需创建专用图层。需要生成三维内容的图层应保持可见且未冻结；关闭、冻结或单独设为不可见的块不会参与识别，锁定图层仍会正常扫描。程序会从每个有效顶层块继续展开组合块中的子块。
                </p>
              </div>
            </li>
            <li>
              <span className="dxf-protocol-step-number">02</span>
              <div>
                <strong>用货架块标出每个货仓</strong>
                <p>
                  托盘货仓使用名称以 <code>横梁式货架</code> 开头的块，料箱货仓使用名称以{" "}
                  <code>料箱货架</code>{" "}
                  开头的块。它们可以直接放置，也可以作为普通组合块的子块。块内可见二维实体的整体边界必须准确覆盖当前货仓；料箱货架块允许用嵌套的可见子块共同组成边界。程序会组合父子变换得到每个货仓各自的真实长宽，边界较长的一边作为货架长度方向。
                  标准 CAD
                  动态块的匿名实例名会自动还原为真实块名后参与前缀匹配，边界仍读取当前实例实际引用的块定义。
                </p>
              </div>
            </li>
            <li>
              <span className="dxf-protocol-step-number">03</span>
              <div>
                <strong>用重叠区域连接连续货架</strong>
                <p>
                  同一类型的相邻货仓需要属于一组连续货架时，让两个货架块只在共用立柱区域产生正面积重叠。连续货仓必须保持直线，不能形成转角、分叉或闭环。程序从重叠链判断整组方向；没有相邻重叠的孤立货仓才读取自身块变换后的朝向。
                </p>
              </div>
            </li>
            <li>
              <span className="dxf-protocol-step-number">04</span>
              <div>
                <strong>用货物标记设置初始货位</strong>
                <p>
                  托盘货仓内部放置名称以 <code>托盘</code> 开头的块，料箱货仓内部放置名称以{" "}
                  <code>料箱</code>{" "}
                  开头的块，每个货仓至少放置一个。程序用标记块完整变换后的中心点判断归属，仓内标记数量就是该仓的初始货位数量。托盘和料箱标记都可以使用三轴镜像。
                  不属于任何同类型货仓的标记会作为普通 CAD
                  内容直接跳过，因此名称以“托盘”开头但位于横梁式货仓外的设备块不会计入货位；同时属于多个货仓的标记会报告协议错误。标准
                  CAD 动态块的真实名称同样参与货物前缀匹配。
                  料箱标记在货仓宽度方向上有几条不同的中心线，就识别为几排；同一排的标记应保持在同一宽度中心线上。导入后料箱货架默认生成
                  10 层，并将所有层的货位铺满料箱。
                </p>
              </div>
            </li>
            <li>
              <span className="dxf-protocol-step-number">05</span>
              <div>
                <strong>放置设备和输送机</strong>
                <p>
                  先进入设备模型管理页，上传 GLB 并填写 DXF 图块匹配前缀；GLB
                  文件名和型号显示名称不参与识别。随后在任意可识别图层插入名称以该前缀开头的顶层设备块。多个前缀同时命中时使用最长前缀，同一前缀可以上传多个可切换型号，数据库列表中的第一个是初次导入的默认型号。标准
                  CAD
                  动态块产生的匿名实例名会由程序沿图纸元数据自动还原。模型定位仍使用当前实例的真实二维边界、插入点和旋转，保持原始尺寸并让底部贴合水平面。
                </p>
                <p>
                  双链输送机使用名称以 <code>双链输送机</code>{" "}
                  开头的顶层块，不需要在设备目录中配置模型。每个实例都根据自身实际块定义的可见边界计算长度、宽度和中心，再读取插入点与旋转角生成组合模型。标准
                  CAD 动态块的匿名实例同样会自动还原名称。
                </p>
                <p>
                  辊道输送机使用名称以 <code>辊道输送机</code>{" "}
                  开头的顶层块，同样不需要在设备目录中配置模型。程序按每个实例自身块定义的可见边界计算长度、宽度和中心，并使用插入点与旋转角生成辊道输送机组合模型。标准
                  CAD 动态块的匿名实例同样会自动还原名称。
                </p>
                <p>
                  辊道顶升移载使用名称以 <code>辊道顶升移载</code>{" "}
                  开头的顶层块，同样不需要在设备目录中配置模型。程序按当前实例的可见二维边界计算长度、宽度和中心，并使用插入点与旋转角生成组合模型。标准
                  CAD 动态块的匿名实例同样会自动还原名称。
                </p>
                <p>
                  托盘往复式提升机使用名称以 <code>托盘往复式提升机</code>{" "}
                  开头的顶层块。程序使用块内可见二维实体边界中心定位，读取插入点和旋转角确定位置与朝向；三维模型保持原始尺寸并让底部贴合水平面。新导入时默认
                  4 层，每层固定 2000 mm，双击模型后可修改当前实例层数。
                </p>
                <p>
                  单工位往复式穿梭车使用名称以 <code>单工位往复式穿梭车</code>{" "}
                  开头的顶层块。程序使用块内可见二维实体的边界长边确定轨道长度，并用边界中心、插入点和旋转角确定位置与朝向。轨道贴地，车身从轨道上表面开始向上缩放，顶部始终贴合车身；默认车身高度为
                  400 mm，双击模型后可修改当前实例高度。
                </p>
                <p>
                  托盘堆垛机使用名称以 <code>托盘堆垛机</code>{" "}
                  开头的顶层块，也可放入批量设定区域中。
                  图块可见边界的长边决定天轨和地轨长度，中心、插入点和旋转决定位置与朝向。
                  模型从下往上依次为地轨、底部、立柱、顶部、天轨；默认立柱高度 8000
                  mm，载货台底面位于立柱高度的 36% 处。
                  双击模型或内部空隙选中后，可在右侧修改立柱高度，也可同类多选或通过区域设定统一修改。
                  高度会保存到项目中。此类图块不会计入托盘货物数量。
                </p>
              </div>
            </li>
            <li>
              <span className="dxf-protocol-step-number">06</span>
              <div>
                <strong>建立批量设定区域</strong>
                <p>
                  创建名称以 <code>批量设定区域</code>{" "}
                  开头的顶层组合块，并把该区域内原有的货架、货物标记、设备、输送机、提升机或穿梭车块放入其中。程序会递归计算组合块全部可见内容的整体边界，不要求额外绘制矩形；区域容器本身不会生成模型，内部对象仍按原规则生成。可编辑对象的中心点落入区域边界或边界线时，就会出现在“区域设定”弹窗。区域不能互相嵌套，同一对象也不能同时落入两个区域。
                </p>
                <p>
                  货架区域的层数可以写成单层 <code>3</code> 或连续范围 <code>3-5</code>
                  ，必须从第 1
                  层开始无断层填写，最后的层编号就是总层数。输入值为不含横杆的净层高；除最顶层外，程序会再加入实际横杆模型高度。货物仍保持原有承托偏移。
                </p>
              </div>
            </li>
            <li>
              <span className="dxf-protocol-step-number">07</span>
              <div>
                <strong>导出并检查协议错误</strong>
                <p>
                  货架、货物标记、三类输送设备、托盘往复式提升机、单工位往复式穿梭车、其他设备和外层组合块都支持
                  X、Y、Z 负缩放。DXF X、Y、Z 分别对应三维对象局部 X、Z、Y
                  轴镜像；竖直镜像后模型底部仍会贴合水平面。缩放不能为
                  0，也不能使用关联阵列；同一连续货架中的货仓不要混用正负 Z 缩放。组合块中的子块放在{" "}
                  <code>0</code>{" "}
                  图层时会继承父块的图层状态。导入后分别核对两类连续货架、三类输送设备、托盘往复式提升机、单工位往复式穿梭车和其他设备数量；循环嵌套、货物标记多重归属、货仓缺少有效标记、货架转角、分叉或完全重合都会显示明确错误。
                </p>
              </div>
            </li>
          </ol>
        </div>
      </details>

      {state.metadata?.isUnitFallback ? (
        <p className="dxf-reference-warning">文件未声明单位，当前按毫米换算。</p>
      ) : null}
      {state.error ? <p className="dxf-reference-error">{state.error}</p> : null}
    </section>
  );
}

function CargoSelect({
  disabled,
  label,
  onChange,
  rackType,
  value
}: {
  disabled: boolean;
  label: string;
  onChange: (asset?: CargoEditorCargoAsset) => Promise<void>;
  rackType: CargoEditorRackType;
  value?: CargoEditorCargoAsset;
}) {
  const options = rackType === "bin" ? binCargoOptions : palletCargoOptions;
  return (
    <label className="cargo-settings-field">
      <span>{label}</span>
      <div className="cargo-settings-select">
        <select
          disabled={disabled}
          onChange={(event) => {
            void onChange(parseCargoAsset(event.target.value, rackType));
          }}
          value={value ?? ""}
        >
          {options.map((option) => (
            <option key={option.value || "empty"} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown aria-hidden="true" size={16} strokeWidth={1.8} />
      </div>
    </label>
  );
}

function createRackConfigurationDraft(
  configuration: CargoEditorRackConfiguration
): RackConfigurationDraft {
  return {
    cargoCapacity: String(configuration.cargoCapacity),
    layerCount: String(configuration.layerHeights.length),
    layerHeights: configuration.layerHeights.map((height) => String(Math.round(height * 1000)))
  };
}

function getDefaultLayerHeightMillimeters(rackType: CargoEditorRackType) {
  const layerHeights =
    rackType === "bin" ? defaultBinRackLayerHeights : defaultPalletRackLayerHeights;
  return Math.round((layerHeights[0] ?? 1.3) * 1000);
}

function parseRackConfiguration(
  draft: RackConfigurationDraft
): CargoEditorRackConfiguration | undefined {
  const cargoCapacity = Number(draft.cargoCapacity);
  const layerCount = Number(draft.layerCount);
  const layerHeights = draft.layerHeights.map(Number);
  if (
    !Number.isInteger(cargoCapacity) ||
    cargoCapacity <= 0 ||
    !Number.isInteger(layerCount) ||
    layerCount <= 0 ||
    layerHeights.length !== layerCount ||
    layerHeights.some((height) => !Number.isInteger(height) || height <= 0)
  ) {
    return;
  }

  return {
    cargoCapacity,
    cargoCellLength: 0,
    cargoCellWidth: 0,
    cargoRowCount: 0,
    layerHeights: layerHeights.map((height) => height / 1000),
    lengthCellCount: 0
  };
}

function updateRackConfigurationDraft(
  draft: RackConfigurationDraft,
  configuration: CargoEditorRackConfiguration,
  setDraft: (draft: RackConfigurationDraft) => void,
  onChange: (configuration: CargoEditorRackConfiguration) => void
) {
  setDraft(draft);
  const parsed = parseRackConfiguration(draft);
  if (!parsed) {
    return;
  }
  onChange({
    ...parsed,
    cargoCellLength: configuration.cargoCellLength,
    cargoCellWidth: configuration.cargoCellWidth,
    cargoRowCount: configuration.cargoRowCount,
    lengthCellCount: configuration.lengthCellCount
  });
}

function ReadonlyValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="cargo-settings-readonly-value">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function parseCargoAsset(
  value: string,
  rackType: CargoEditorRackType
): CargoEditorCargoAsset | undefined {
  if (rackType === "bin" && value === ModelAsset.BinCargo) {
    return value;
  }
  if (
    rackType === "pallet" &&
    (value === ModelAsset.WoodenCrate || value === ModelAsset.SteelCoil)
  ) {
    return value;
  }
}

function formatDxfStage(stage?: CargoEditorDxfStage) {
  switch (stage) {
    case "reading":
      return "正在读取文件";
    case "parsing":
      return "正在解析 DXF";
    case "rasterizing":
      return "正在生成平面图纸";
    default:
      return "正在准备图纸";
  }
}

function formatMeters(value: number) {
  return `${value.toFixed(2)}m`;
}
